/**
 * AddressFields — Country-aware structured address form with:
 *   • Dynamic field labels per country (Post Code vs Zip Code vs Eircode etc.)
 *   • Postcode → address lookup  (postcodes.io for UK, zippopotam.us worldwide)
 *   • City autocomplete          (OpenStreetMap Nominatim, debounced, country-scoped)
 *
 * Used by CustomerDetailPage (Partners) and TenantDetailPage (Tenants).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Search, MapPin, AlertCircle } from 'lucide-react';

const NAVY = 'hsl(var(--c4p-navy-900))';

// ── Country → address field labels ────────────────────────────────────────────
const ADDRESS_CONFIG = {
  'United Kingdom':     { city: 'City / Town',        region: 'County',              postcode: 'Post Code',    iso2: 'GB' },
  'Ireland':            { city: 'City / Town',        region: 'County',              postcode: 'Eircode',      iso2: 'IE' },
  'Malta':              { city: 'Town',               region: null,                  postcode: 'Postal Code',  iso2: 'MT' },
  'United States':      { city: 'City',               region: 'State',               postcode: 'Zip Code',     iso2: 'US' },
  'Canada':             { city: 'City',               region: 'Province',            postcode: 'Postal Code',  iso2: 'CA' },
  'Mexico':             { city: 'City / Municipality',region: 'State',               postcode: 'Postal Code',  iso2: 'MX' },
  'Brazil':             { city: 'City',               region: 'State',               postcode: 'CEP',          iso2: 'BR' },
  'Australia':          { city: 'City / Suburb',      region: 'State / Territory',   postcode: 'Postcode',     iso2: 'AU' },
  'New Zealand':        { city: 'City / Town',        region: 'Region',              postcode: 'Postcode',     iso2: 'NZ' },
  'India':              { city: 'City',               region: 'State',               postcode: 'PIN Code',     iso2: 'IN' },
  'Singapore':          { city: 'City',               region: null,                  postcode: 'Postal Code',  iso2: 'SG' },
  'Japan':              { city: 'City',               region: 'Prefecture',          postcode: 'Postal Code',  iso2: 'JP' },
  'Hong Kong':          { city: 'District',           region: null,                  postcode: null,           iso2: 'HK' },
  'China':              { city: 'City',               region: 'Province',            postcode: 'Postal Code',  iso2: 'CN' },
  'South Korea':        { city: 'City',               region: 'Province',            postcode: 'Postal Code',  iso2: 'KR' },
  'Malaysia':           { city: 'City',               region: 'State',               postcode: 'Postal Code',  iso2: 'MY' },
  'Indonesia':          { city: 'City',               region: 'Province',            postcode: 'Postal Code',  iso2: 'ID' },
  'Philippines':        { city: 'City',               region: 'Province',            postcode: 'ZIP Code',     iso2: 'PH' },
  'Thailand':           { city: 'City / District',    region: 'Province',            postcode: 'Postal Code',  iso2: 'TH' },
  'Taiwan':             { city: 'City',               region: 'County',              postcode: 'Postal Code',  iso2: 'TW' },
  'Vietnam':            { city: 'City / District',    region: 'Province',            postcode: 'Postal Code',  iso2: 'VN' },
  'Pakistan':           { city: 'City',               region: 'Province',            postcode: 'Postal Code',  iso2: 'PK' },
  'UAE':                { city: 'City',               region: 'Emirate',             postcode: null,           iso2: 'AE' },
  'Saudi Arabia':       { city: 'City',               region: 'Region',              postcode: 'Postal Code',  iso2: 'SA' },
  'Qatar':              { city: 'City',               region: null,                  postcode: null,           iso2: 'QA' },
  'Kuwait':             { city: 'City',               region: 'Governorate',         postcode: null,           iso2: 'KW' },
  'Bahrain':            { city: 'City',               region: 'Governorate',         postcode: null,           iso2: 'BH' },
  'Oman':               { city: 'City',               region: 'Governorate',         postcode: 'Postal Code',  iso2: 'OM' },
  'Jordan':             { city: 'City',               region: 'Governorate',         postcode: 'Postal Code',  iso2: 'JO' },
  'Israel':             { city: 'City',               region: null,                  postcode: 'Postal Code',  iso2: 'IL' },
  'Turkey':             { city: 'City',               region: 'Province',            postcode: 'Postal Code',  iso2: 'TR' },
  'Egypt':              { city: 'City',               region: 'Governorate',         postcode: 'Postal Code',  iso2: 'EG' },
  'Germany':            { city: 'City',               region: 'State',               postcode: 'Postal Code',  iso2: 'DE' },
  'France':             { city: 'City',               region: 'Department',          postcode: 'Postal Code',  iso2: 'FR' },
  'Netherlands':        { city: 'City',               region: 'Province',            postcode: 'Postal Code',  iso2: 'NL' },
  'Belgium':            { city: 'City',               region: 'Province',            postcode: 'Postal Code',  iso2: 'BE' },
  'Switzerland':        { city: 'City',               region: 'Canton',              postcode: 'Postal Code',  iso2: 'CH' },
  'Austria':            { city: 'City',               region: 'State',               postcode: 'Postal Code',  iso2: 'AT' },
  'Italy':              { city: 'City',               region: 'Province',            postcode: 'CAP',          iso2: 'IT' },
  'Spain':              { city: 'City',               region: 'Province',            postcode: 'Postal Code',  iso2: 'ES' },
  'Portugal':           { city: 'City',               region: 'District',            postcode: 'Postal Code',  iso2: 'PT' },
  'Norway':             { city: 'City / Municipality',region: 'County',              postcode: 'Postal Code',  iso2: 'NO' },
  'Sweden':             { city: 'City',               region: 'County',              postcode: 'Postal Code',  iso2: 'SE' },
  'Denmark':            { city: 'City',               region: 'Region',              postcode: 'Postal Code',  iso2: 'DK' },
  'Finland':            { city: 'City',               region: 'Region',              postcode: 'Postal Code',  iso2: 'FI' },
  'Poland':             { city: 'City',               region: 'Province',            postcode: 'Postal Code',  iso2: 'PL' },
  'Czech Republic':     { city: 'City',               region: 'Region',              postcode: 'Postal Code',  iso2: 'CZ' },
  'Romania':            { city: 'City',               region: 'County',              postcode: 'Postal Code',  iso2: 'RO' },
  'Hungary':            { city: 'City',               region: 'County',              postcode: 'Postal Code',  iso2: 'HU' },
  'Greece':             { city: 'City',               region: 'Region',              postcode: 'Postal Code',  iso2: 'GR' },
  'Ukraine':            { city: 'City',               region: 'Oblast',              postcode: 'Postal Code',  iso2: 'UA' },
  'Luxembourg':         { city: 'City',               region: null,                  postcode: 'Postal Code',  iso2: 'LU' },
  'South Africa':       { city: 'City',               region: 'Province',            postcode: 'Postal Code',  iso2: 'ZA' },
  'Nigeria':            { city: 'City',               region: 'State',               postcode: 'Postal Code',  iso2: 'NG' },
  'Kenya':              { city: 'City',               region: 'County',              postcode: 'Postal Code',  iso2: 'KE' },
};

const DEFAULT_CONFIG = { city: 'City', region: 'State / Region', postcode: 'Postal Code', iso2: null };

export function getAddressConfig(country) {
  return ADDRESS_CONFIG[country] || DEFAULT_CONFIG;
}

export function formatAddress(record) {
  if (!record) return '';
  const hasStructured = record.addr_line1 || record.addr_city;
  if (!hasStructured) return record.billing_address || '';
  const lines = [];
  if (record.addr_line1) lines.push(record.addr_line1);
  if (record.addr_line2) lines.push(record.addr_line2);
  if (record.addr_city) lines.push(record.addr_city);
  const regionPostcode = [record.addr_region, record.addr_postcode].filter(Boolean).join('  ');
  if (regionPostcode) lines.push(regionPostcode);
  if (record.country) lines.push(record.country);
  return lines.join('\n');
}

// ── Irish Eircode routing-key lookup ──────────────────────────────────────────
// Each routing key encodes the postal area. zippopotam.us has no Eircode data
// so we use a static table compiled from An Post / Eircode public information.
// Format: 'ROUTING_KEY': { city, region (county) }
const EIRCODE_MAP = {
  // Dublin city & county (D01–D24)
  D01:'Dublin',D02:'Dublin',D03:'Dublin',D04:'Dublin',D05:'Dublin',
  D06:'Dublin',D07:'Dublin',D08:'Dublin',D09:'Dublin',D10:'Dublin',
  D11:'Dublin',D12:'Dublin',D13:'Dublin',D14:'Dublin',D15:'Dublin',
  D16:'Dublin',D17:'Dublin',D18:'Dublin',D20:'Dublin',D22:'Dublin',D24:'Dublin',
  // Cork city (T routing keys)
  T12:'Cork',T23:'Cork',T34:'Cork',T45:'Cork',T56:'Cork',
  // Cork county (C)
  C15:'Carrigaline',C31:'Mallow',
  // Galway (H91 = Galway City, others = county towns)
  H91:'Galway',
  // Limerick city & county
  E34:'Limerick',E45:'Limerick',
  // Waterford
  X35:'Waterford',X42:'Waterford',X91:'Waterford',
  // Kilkenny
  R95:'Kilkenny',
  // Wexford
  Y14:'Enniscorthy',Y21:'Gorey',Y25:'Wexford',Y35:'New Ross',
  // Carlow
  R21:'Carlow',R32:'Carlow',
  // Wicklow
  A63:'Wicklow',A67:'Wicklow',A98:'Bray',
  // Kildare
  A41:'Celbridge',A42:'Naas',A45:'Naas',
  K32:'Naas',K34:'Athy',K36:'Newbridge',K45:'Kildare',K56:'Maynooth',K67:'Monasterevin',K78:'Athy',
  // Meath
  A84:'Trim',A85:'Trim',A86:'Navan',A87:'Navan',A88:'Drogheda',A92:'Laytown',
  N41:'Navan',
  // Louth
  H62:'Dundalk',A91:'Drogheda',
  // Monaghan
  H18:'Monaghan',
  // Cavan
  H12:'Cavan',H53:'Cavan',
  // Longford
  N39:'Longford',
  // Westmeath
  N91:'Mullingar',
  // Offaly
  R35:'Tullamore',
  // Laois
  R32:'Portlaoise',
  // Tipperary
  E25:'Nenagh',E32:'Clonmel',E41:'Tipperary',E53:'Thurles',
  // Clare
  E65:'Ennis',V95:'Kilrush',
  // Kerry
  V14:'Killarney',V15:'Tralee',V23:'Tralee',V31:'Kenmare',
  V92:'Tralee',V93:'Killarney',V94:'Killarney',
  // Mayo
  F12:'Castlebar',F23:'Ballina',F26:'Westport',F28:'Claremorris',
  F31:'Ballinrobe',F35:'Swinford',
  // Roscommon
  F42:'Roscommon',F45:'Boyle',F52:'Strokestown',
  // Sligo
  F91:'Sligo',
  // Leitrim
  N41:'Carrick-on-Shannon',
  // Donegal
  F92:'Letterkenny',F93:'Donegal',F94:'Ballybofey',
  // Galway county towns
  H14:'Tuam',H23:'Ballinasloe',H54:'Loughrea',H65:'Gort',
};

const EIRCODE_COUNTY_MAP = {
  D:'County Dublin',
  T:'County Cork',C:'County Cork',
  H:'County Galway',
  E:'County Limerick',
  X:'County Waterford',
  W:'County Waterford',
  Y:'County Wexford',
  R:'County Carlow',
  A:'County Wicklow',
  K:'County Kildare',
  N:'County Meath',
  V:'County Kerry',
  F:'County Connacht',
  P:'County Cork',
};

function lookupEircode(pc) {
  // Routing key = first 3 chars (letter + 2 digits), e.g. D08 from "D08R9CN"
  const stripped = pc.replace(/\s/g, '').toUpperCase();
  const routingKey = stripped.slice(0, 3);
  const letter = routingKey[0];

  const cityName = EIRCODE_MAP[routingKey];
  // For Dublin D01-D24, city = "Dublin", county by district
  if (letter === 'D') {
    return { city: cityName || 'Dublin', region: 'County Dublin' };
  }
  if (cityName) {
    const region = EIRCODE_COUNTY_MAP[letter] || '';
    return { city: cityName, region };
  }
  // Unknown routing key — return county estimate from first letter
  const region = EIRCODE_COUNTY_MAP[letter];
  if (region) return { city: '', region };
  return null;
}

// ── Postcode lookup ────────────────────────────────────────────────────────────
async function lookupPostcode(postcode, country, iso2) {
  const pc = postcode.trim().replace(/\s+/g, '').toUpperCase();
  if (!pc) return null;

  // Ireland: Eircode static routing-key lookup (zippopotam.us has no Eircode data)
  if (country === 'Ireland' || iso2 === 'IE') {
    return lookupEircode(pc);
  }

  // Malta: locality-code static lookup (zippopotam.us has no Malta data)
  // Format: 3-letter locality code + 4 digits, e.g. STJ 3110
  if (country === 'Malta' || iso2 === 'MT') {
    const localityCode = pc.slice(0, 3).toUpperCase();
    const MALTA_MAP = {
      ATD:'Attard',   BAL:'Balzan',    BIR:'Bir',      BKR:'Birkirkara',
      BZN:'Balzan',   CMT:'Comino',    DGL:'San Ġwann',
      FGR:'Fgura',    FLK:'Floriana',  FRN:'Fontana',
      GHB:'Għarb',    GHD:'Għasri',    GHR:'Żebbuġ (Gozo)',
      GRG:'Gżira',    GSM:'Gudja',     GXQ:'Għajnsielem',
      HAM:'Ħamrun',   KKP:'Kirkop',    KNK:'Kalkara',
      LJA:'Iklin',    LMR:'Lija',      LQM:'Mqabba',
      MDN:'Mdina',    MGR:'Marsa',     MLH:'Mellieħa',
      MNR:'Munxar',   MRS:'Marsaxlokk',MSR:'Msida',
      MST:'Mosta',    MXK:'Mġarr',     MXL:'Mġarr (Gozo)',
      NDN:'Nadur',    NXR:'Naxxar',    PBK:'Pembroke',
      PLA:'Paola',    PLQ:'Pietà',     QLA:'Qala',
      QRM:'Qormi',    QRN:'Qrendi',    RAB:'Rabat',
      SAF:"Saint Paul's Bay",          SGN:'Senglea',
      SKL:'Marsaskala',SLM:'Sliema',   SRQ:'Siġġiewi',
      STJ:"Saint Julian's",            SVR:'Swieqi',
      TRX:'Tarxien',  VCT:'Victoria',  VLT:'Valletta',
      XGH:'Xgħajra', ZBR:'Żabbar',    ZBG:'Żebbuġ',
      ZRQ:'Żejtun',   ZTN:'Żurrieq',
    };
    const city = MALTA_MAP[localityCode];
    // Gozo island localities get 'Gozo' as region
    const gozoLocalities = new Set(['VCT','GXQ','GHB','GHD','GHR','MXL','NDN','QLA','FRN','MNR','XKL','ZGR','SBT']);
    if (city) return { city, region: gozoLocalities.has(localityCode) ? 'Gozo' : 'Malta' };
    return null;
  }

  // UK: use postcodes.io for best accuracy
  if (country === 'United Kingdom' || iso2 === 'GB') {
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`);
      const data = await res.json();
      if (data.status === 200 && data.result) {
        const r = data.result;
        const rawParish = (r.parish || '').replace(
          /, unparished area$|, City of$| Town$| CP$| Civil Parish$| Parish Council$| Borough$| District$/i,
          ''
        ).trim();
        const city = rawParish && rawParish.toLowerCase() !== (r.admin_district || '').toLowerCase()
          ? rawParish
          : (r.admin_district || '').replace(/, City of$/i, '').trim();
        const region = r.admin_county || r.region || '';
        return { city, region };
      }
    } catch { /* fall through */ }
  }

  // Worldwide: zippopotam.us (supports 60+ countries)
  // Canada: zippopotam.us only recognises the FSA (first 3 chars of the postal code)
  if (iso2) {
    const lookupCode = iso2 === 'CA' ? pc.slice(0, 3) : pc;
    try {
      const res = await fetch(`https://api.zippopotam.us/${iso2.toLowerCase()}/${encodeURIComponent(lookupCode)}`);
      if (res.ok) {
        const data = await res.json();
        const place = (data.places || [])[0];
        if (place) {
          return {
            city:   place['place name'] || '',
            region: place['state'] || '',
          };
        }
      }
    } catch { /* fall through */ }
  }

  return null;
}

// ── City autocomplete via Nominatim (OSM) ─────────────────────────────────────
async function searchCities(query, country) {
  if (!query || query.length < 2) return [];
  try {
    const params = new URLSearchParams({
      format: 'json',
      city: query,
      addressdetails: '1',
      limit: '8',
      featuretype: 'city',
    });
    if (country) params.set('countrycodes', getAddressConfig(country).iso2?.toLowerCase() || '');
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?${params}`,
      { headers: { 'Accept-Language': 'en', 'User-Agent': 'C4P-Quoting-Portal/1.0' } }
    );
    const data = await res.json();
    // Extract unique city names with their state/county
    const seen = new Set();
    return data
      .filter(d => d.address?.city || d.address?.town || d.address?.village || d.address?.municipality)
      .map(d => {
        const city = d.address.city || d.address.town || d.address.village || d.address.municipality || '';
        const region = d.address.county || d.address.state || d.address.province || '';
        const postcode = d.address.postcode || '';
        return { city, region, postcode, display: city + (region ? `, ${region}` : '') };
      })
      .filter(d => { if (seen.has(d.city)) return false; seen.add(d.city); return true; })
      .slice(0, 6);
  } catch {
    return [];
  }
}

// ── Main component ─────────────────────────────────────────────────────────────
export function AddressFields({ form, setForm }) {
  const cfg = getAddressConfig(form.country);
  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const hasLegacy = form.billing_address && !form.addr_line1 && !form.addr_city;

  // Postcode lookup state
  const [lookingUp, setLookingUp]     = useState(false);
  const [lookupMsg, setLookupMsg]     = useState('');
  const [lookupError, setLookupError] = useState(false);

  // City autocomplete state
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityFocused, setCityFocused]         = useState(false);
  const [cityLoading, setCityLoading]         = useState(false);
  const cityDebounceRef = useRef(null);
  const dropdownRef = useRef(null);

  // Reset lookup message when postcode changes
  useEffect(() => { setLookupMsg(''); setLookupError(false); }, [form.addr_postcode, form.country]);

  // Debounced city search
  const handleCityChange = (val) => {
    set('addr_city', val);
    setLookupMsg(''); // clear postcode message
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    if (!val || val.length < 2) { setCitySuggestions([]); return; }
    cityDebounceRef.current = setTimeout(async () => {
      setCityLoading(true);
      const results = await searchCities(val, form.country);
      setCitySuggestions(results);
      setCityLoading(false);
    }, 350);
  };

  const handleCitySelect = (suggestion) => {
    setForm(f => ({
      ...f,
      addr_city:    suggestion.city,
      addr_region:  suggestion.region  || f.addr_region,
      addr_postcode: suggestion.postcode || f.addr_postcode,
    }));
    setCitySuggestions([]);
    setCityFocused(false);
  };

  const handlePostcodeLookup = useCallback(async () => {
    const pc = (form.addr_postcode || '').trim();
    if (!pc) return;
    setLookingUp(true); setLookupMsg(''); setLookupError(false);
    const result = await lookupPostcode(pc, form.country, cfg.iso2);
    if (result) {
      setForm(f => ({
        ...f,
        addr_city:   result.city   || f.addr_city,
        addr_region: result.region || f.addr_region,
      }));
      setLookupMsg('Address fields filled from postcode lookup');
      setLookupError(false);
    } else {
      setLookupMsg(`No match found for "${pc}". Please fill in city and region manually.`);
      setLookupError(true);
    }
    setLookingUp(false);
  }, [form.addr_postcode, form.country, cfg.iso2, setForm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setCitySuggestions([]); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const showPostcodeLookup = cfg.postcode !== null && cfg.iso2; // kept for lookup message logic

  return (
    <div className="rounded-xl border p-4 space-y-3" style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: NAVY }}>Address</p>
        {hasLegacy && (
          <p className="text-xs" style={{ color: '#94a3b8' }}>
            Legacy address stored — fill fields below to replace it
          </p>
        )}
      </div>

      {/* Line 1 */}
      <div className="space-y-1.5">
        <Label className="text-xs">Address Line 1</Label>
        <Input value={form.addr_line1 || ''} onChange={e => set('addr_line1', e.target.value)}
          placeholder="Building / street address" data-testid="addr-line1" />
      </div>

      {/* Line 2 */}
      <div className="space-y-1.5">
        <Label className="text-xs">
          Address Line 2 <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span>
        </Label>
        <Input value={form.addr_line2 || ''} onChange={e => set('addr_line2', e.target.value)}
          placeholder="Suite, floor, building name…" data-testid="addr-line2" />
      </div>

      {/* City with autocomplete */}
      <div className="space-y-1.5 relative" ref={dropdownRef}>
        <Label className="text-xs">{cfg.city}</Label>
        <div className="relative">
          <Input
            value={form.addr_city || ''}
            onChange={e => handleCityChange(e.target.value)}
            onFocus={() => setCityFocused(true)}
            onBlur={() => setTimeout(() => setCityFocused(false), 200)}
            placeholder={cfg.city}
            autoComplete="off"
            className="h-10"
            data-testid="addr-city"
          />
          {cityLoading && (
            <Loader2 size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin"
              style={{ color: '#94a3b8' }} />
          )}
        </div>
        {/* City suggestions dropdown */}
        {citySuggestions.length > 0 && cityFocused && (
          <div className="absolute z-50 left-0 right-0 bg-white rounded-xl border shadow-lg overflow-hidden mt-0.5"
            style={{ borderColor: '#e2e8f0', maxHeight: '240px', overflowY: 'auto' }}>
            {citySuggestions.map((s, i) => (
              <button key={i} type="button"
                className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
                onMouseDown={() => handleCitySelect(s)}
                data-testid={`city-suggestion-${i}`}>
                <MapPin size={12} className="flex-shrink-0 mt-0.5" style={{ color: '#94a3b8' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: NAVY }}>{s.city}</p>
                  {s.region && <p className="text-xs" style={{ color: '#94a3b8' }}>{s.region}</p>}
                </div>
              </button>
            ))}
            <p className="px-3 py-1.5 text-xs border-t" style={{ color: '#cbd5e1', borderColor: '#f1f5f9' }}>
              Powered by OpenStreetMap
            </p>
          </div>
        )}
      </div>

      {/* Region + Postcode */}
      <div className="grid grid-cols-2 gap-3">
        {cfg.region !== null && (
          <div className="space-y-1.5">
            <div className="h-5 flex items-center">
              <Label className="text-xs">{cfg.region}</Label>
            </div>
            <Input value={form.addr_region || ''} onChange={e => set('addr_region', e.target.value)}
              placeholder={cfg.region} className="h-10" data-testid="addr-region" />
          </div>
        )}
        {cfg.postcode !== null && (
          <div className="space-y-1.5">
            <div className="h-5 flex items-center">
              <Label className="text-xs">{cfg.postcode}</Label>
            </div>
            {/* Input + Look Up button side-by-side */}
            <div className="flex gap-2 items-center">
              <Input value={form.addr_postcode || ''} onChange={e => set('addr_postcode', e.target.value.toUpperCase())}
                placeholder={cfg.postcode} className="font-mono flex-1 h-10"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handlePostcodeLookup(); }}}
                data-testid="addr-postcode" />
              {cfg.iso2 && (
                <button type="button" onClick={handlePostcodeLookup} disabled={lookingUp || !form.addr_postcode}
                  className="flex items-center gap-1.5 px-3 rounded-lg text-xs font-semibold flex-shrink-0 transition-all h-10"
                  style={{
                    background: lookingUp || !form.addr_postcode ? '#f1f5f9' : '#16a34a',
                    color: lookingUp || !form.addr_postcode ? '#94a3b8' : 'white',
                    border: `1.5px solid ${lookingUp || !form.addr_postcode ? '#e2e8f0' : '#16a34a'}`,
                    cursor: lookingUp || !form.addr_postcode ? 'not-allowed' : 'pointer',
                  }}
                  data-testid="addr-postcode-lookup">
                  {lookingUp
                    ? <Loader2 size={12} className="animate-spin" />
                    : <Search size={12} />}
                  {lookingUp ? 'Looking…' : 'Look Up'}
                </button>
              )}
            </div>
          </div>
        )}
        {cfg.region !== null && cfg.postcode === null && (
          <div />
        )}
      </div>

      {/* Lookup result message */}
      {lookupMsg && (
        <div className="flex items-start gap-1.5 text-xs rounded-lg px-3 py-2"
          style={{
            background: lookupError ? '#fef2f2' : '#f0fdf4',
            color: lookupError ? '#991b1b' : '#15803d',
            border: `1px solid ${lookupError ? '#fecaca' : '#bbf7d0'}`,
          }}>
          {lookupError
            ? <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
            : <MapPin size={12} className="flex-shrink-0 mt-0.5" />}
          {lookupMsg}
        </div>
      )}

      {/* Legacy address note */}
      {hasLegacy && (
        <details className="mt-1">
          <summary className="text-xs cursor-pointer" style={{ color: '#94a3b8' }}>
            Show legacy address (for reference)
          </summary>
          <div className="mt-2 rounded-lg p-3 text-xs whitespace-pre-wrap"
            style={{ background: '#f1f5f9', color: '#64748b', fontFamily: 'monospace' }}>
            {form.billing_address}
          </div>
        </details>
      )}
    </div>
  );
}
